import React, { useEffect, useState, useMemo, useCallback } from "react";
import styles from "./scqTemp.module.css";
import {
  extractGivenAnswers,
  getCharacterFromNumber,
  hasLongString,
  isNotEmptyOrNull,
  SCQ_BREAKPOINTS } from
"../../../utils/common-utils";
import { useRoot } from "../../../context/RootContext";
import { useTutor } from "../../../context/AiTutorContext";
import useStateRef from "react-usestateref";
import { getPresentationModeBasedOnTask } from "../../../utils/presentationModes";
import { taskTypes } from "../../../utils/LearningPathUtils";
import { renderContent } from "../../../utils/TutorConstants";
import CheckMarkCircle from "../../../assets/svgs/checkmarkCircle.svg";
import CloseCircle from "../../../assets/svgs/closeCircle.svg";
import ImageModal from "../../atoms/ImageModal";
import {
  answerEncryptText,
  answers_index_hashed_keys } from
"../../../utils/crypto";

const ScqComponent = (props) => {
  const { isStandaloneWithSubjectMapping, isStandalone, isReviewScreen } =
  useRoot();
  const { isFactual } = useTutor();

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [value, setValue] = useState(
    isNotEmptyOrNull(props?.userAnswer) ?
    extractGivenAnswers(props?.userAnswer)?.[0] :
    ""
  );
  const [selectedIndex, setSelectedIndex] = useState();
  const [optionsData, setOptionsData] = useState(props?.options);
  const [isOpen, setIsOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState();
  const [onAnswerSubmitted, setOnAnswerSubmitted, onAnswerSubmittedRef] =
  useStateRef(
    props?.onAnswerSubmitted ?
    props?.onAnswerSubmitted :
    isNotEmptyOrNull(props?.userAnswer) ?
    true :
    false
  );

  // Add hashed options state
  const [hashedOptions, setHashedOptions] = useState({});
  const [isHashingComplete, setIsHashingComplete] = useState(false);

  const [presentationMode, setPresentationMode] = useState(
    getPresentationModeBasedOnTask(
      props?.currentQuestion?.factual === true ?
      taskTypes?.factualMode :
      isReviewScreen ?
      taskTypes?.reviewMode :
      isFactual ?
      taskTypes?.assessment :
      isStandalone || isStandaloneWithSubjectMapping ?
      taskTypes?.standalone :
      props?.taskType
    )
  );

  // Add hashed options computation effect
  useEffect(() => {
    const computeHashedOptions = async () => {
      if (!optionsData.length) return;

      const hashed = {};
      for (let i = 0; i < optionsData.length; i++) {
        const item = optionsData[i];
        let data = await (isNotEmptyOrNull(
          props?.currentQuestion?.answers_index_hashed_keys
        ) ?
        answers_index_hashed_keys(
          props?.currentQuestion?.uuid + window.ENV.HASH_SECRET_KEY + i
        ) :
        isNotEmptyOrNull(props?.currentQuestion?.answers_key) ?
        answerEncryptText(props?.currentQuestion?.options_layout?.[i]) :
        item);

        hashed[i] = data;
      }
      setHashedOptions(hashed);
      setIsHashingComplete(true);
    };

    computeHashedOptions();
  }, [
  optionsData,
  props?.currentQuestion?.uuid,
  props?.currentQuestion?.answers_index_hashed_keys,
  props?.currentQuestion?.answers_key]
  );

  // Add getHashedOption function
  const getHashedOption = useCallback(
    (item, position) => {
      if (!isHashingComplete) return item; // Fallback while hashing
      return hashedOptions[position] || item;
    },
    [hashedOptions, isHashingComplete]
  );

  const handleOnChange = useCallback(
    (item, index) => {
      setValue(item);
      setSelectedIndex(index);

      // Use the memoized hashed option
      let selectedAnswer = getHashedOption(item, index);

      props.handleAnswerChange?.({
        selectedItem: item,
        selectedHashedIndexAnswer: selectedAnswer,
        selectedIndex: index
      });
    },
    [props, getHashedOption]
  );

  const openImage = useCallback((index) => {
    setImageIndex(index);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (props?.onAnswerSubmitted) {
      let selectedAnswer = getHashedOption(value, selectedIndex);
      setValue(selectedAnswer);
      setOnAnswerSubmitted(props?.onAnswerSubmitted);
    }
  }, [props?.onAnswerSubmitted, getHashedOption, value, selectedIndex]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const screenConfig = useMemo(() => {
    const config = SCQ_BREAKPOINTS.find((bp) => windowWidth >= bp.min);
    const isComprehension = props?.isComprehension;

    return {
      characterCount: isComprehension ?
      config.chars.comprehension :
      config.chars.default,
      screenSize: config.size
    };
  }, [windowWidth, props?.isComprehension]);

  const stringLengthAnalysis = useMemo(() => {
    const { characterCount } = screenConfig;
    return {
      hasLongStrings: hasLongString(props?.options, characterCount),
      hasVeryLongStrings: hasLongString(props?.options, characterCount + 2)
    };
  }, [screenConfig, props?.options]);

  const layoutType = useMemo(() => {
    const { screenSize } = screenConfig;
    const { hasLongStrings, hasVeryLongStrings } = stringLengthAnalysis;

    if (screenSize === "mobile") return "column";
    if (screenSize === "tablet") return hasLongStrings ? "column" : "twoRows";

    if (hasVeryLongStrings) return "column";
    if (hasLongStrings) return "twoRows";
    return "row";
  }, [screenConfig, stringLengthAnalysis]);

  useEffect(() => {
    props?.setLayoutType?.(layoutType);
  }, [layoutType, props?.setLayoutType]);

  const getAnswerStyle = useCallback(
    (item, value, answerSubmitted, imagesList, currentQuestion, position) => {
      const hashedItem = getHashedOption(item, position);
      const hashedValue = value ?
      getHashedOption(value, optionsData.indexOf(value)) :
      value;

      const index = props?.displayedQues?.findIndex(
        (item) => item.version_uuid === currentQuestion?.version_uuid
      );

      if (
      currentQuestion?.factual === true ?
      props?.displayedQues?.[index]?.isSubmitted !== true :
      !answerSubmitted)
      {
        if (position === selectedIndex) {
          return imagesList?.length > 0 ?
          "scq-card-selected" :
          "scq-card-selected-noimage";
        } else {
          return imagesList?.length > 0 ? "scq-card" : "scq-card-noimage";
        }
      } else {
        if (hashedItem === props?.answer?.[0]) {
          if (isReviewScreen) {
            return imagesList?.length > 0 ?
            "scq-card-correct" :
            "scq-card-correct-noimage";
          } else if (
          presentationMode.DisplayCorrectAnswerInstantly ||
          presentationMode.DisplayCorrectAnswerAfterNextClick &&
          currentQuestion?.showGreen)
          {
            return imagesList?.length > 0 ?
            "scq-card-correct" :
            "scq-card-correct-noimage";
          } else {
            if (hashedItem === hashedValue) {
              return imagesList?.length > 0 ?
              "scq-card-selected" :
              "scq-card-selected-noimage";
            } else {
              return imagesList?.length > 0 ? "scq-card" : "scq-card-noimage";
            }
          }
        } else if (hashedItem === hashedValue) {
          if (isReviewScreen) {
            return imagesList?.length > 0 ?
            "scq-card-wrong" :
            "scq-card-wrong-noimage";
          } else if (
          presentationMode.DisplayWrongAnswerInstantly ||
          presentationMode.DisplayWrongAnswerAfterNextClick &&
          currentQuestion.showRed)
          {
            return imagesList?.length > 0 ?
            "scq-card-wrong" :
            "scq-card-wrong-noimage";
          } else {
            return props.taskType === taskTypes.assessment || isFactual ?
            imagesList?.length > 0 ?
            "scq-card-selected" :
            "scq-card-selected-noimage" :
            imagesList?.length > 0 ?
            "scq-card-selected" :
            "scq-card-selected-noimage";
          }
        } else {
          return imagesList?.length > 0 ? "scq-card" : "scq-card-noimage";
        }
      }
    },
    [
    getHashedOption,
    optionsData,
    selectedIndex,
    isReviewScreen,
    presentationMode,
    props?.answer,
    props?.taskType,
    isFactual,
    props?.displayedQues]

  );

  // Memoize option styles to prevent multiple calls to getAnswerStyle
  const optionStyles = useMemo(() => {
    if (!isHashingComplete) return {};

    const styles = {};
    optionsData.forEach((item, index) => {
      const style = getAnswerStyle(
        item,
        value,
        onAnswerSubmittedRef.current,
        props?.option_image_details,
        props?.currentQuestion,
        index
      );

      styles[index] = {
        style,
        correctAnswerIndicator:
        style === "scq-card-correct" || style === "scq-card-correct-noimage",
        wrongAnswerIndicator:
        style === "scq-card-wrong" || style === "scq-card-wrong-noimage",
        selectedIndicator:
        style === "scq-card-selected" ||
        style === "scq-card-selected-noimage"
      };
    });

    return styles;
  }, [
  optionsData,
  value,
  onAnswerSubmittedRef.current,
  props?.option_image_details,
  props?.currentQuestion,
  getAnswerStyle,
  isHashingComplete]
  );

  const renderSingleOption = useCallback(
    (optionContent, idx, isColumn = false) => {
      const label = getCharacterFromNumber(idx);

      // Use memoized option styles instead of calling getAnswerStyle directly
      const optionStyle = optionStyles[idx] || {};
      const {
        style: answerStyle,
        correctAnswerIndicator,
        wrongAnswerIndicator,
        selectedIndicator
      } = optionStyle;

      const textColorStyle =
      correctAnswerIndicator || wrongAnswerIndicator ?
      { color: "black" } :
      selectedIndicator ?
      { color: "white" } :
      { color: "#130159" };

      const textClassName = "scq-text";

      return (
        <div
          className={`${styles.optionContainer} ${
          isColumn ? styles.columnOptions : ""} ${
          layoutType === "twoRows" ? styles.twoRowOptions : ""} ${
          props?.isComprehension ? styles?.comprehensionOptionContainer : ""} ${
          styles[answerStyle]}`}
          key={idx}
          onClick={() => {
            if (!onAnswerSubmittedRef.current) {
              handleOnChange(optionContent, idx);
            }
          }}
          style={{
            cursor:
            onAnswerSubmittedRef.current || props?.isDontAllow ?
            "default" :
            "pointer"
          }}
          id={`scq-option-${idx}`}>

          <div
            className={`${styles.optionContainerLeft}`}
            id={`scq-option-content-${idx}`}>

            {/* LABEL */}
            <div id={`scq-option-label-${idx}`} className={styles.character}>
              {label}
            </div>
            {/* OPTION DETAILS*/}
            <div
              className={`${styles.optionDetails} ${styles[textClassName]}`}
              style={textColorStyle}
              id={`scq-option-text-wrapper-${idx}`}>

              {/* <div>{optionContent}</div> */}
              <div id={`scq-option-text-${idx}`}>
                {renderContent(props?.modifiedOptions?.[idx] || optionContent)}
              </div>
              {/* Image Options */}
              {props?.option_image_details?.length > 0 &&
              props?.option_image_details[idx] &&
              <div
                className={styles.optionImage}
                id={`scq-option-image-container-${idx}`}>

                    <img
                  src={props?.option_image_details[idx].url}
                  onClick={(e) => {
                    e.stopPropagation();
                    openImage(idx);
                  }}
                  className={styles["scq-image"]}
                  alt={`Image ${idx}`}
                  style={{ cursor: "pointer" }}
                  id={`scq-option-image-${idx}`} />

                  </div>
              }
            </div>
          </div>
          {/* RIGHT/ WRONG ICON  */}
          <div
            className={styles?.optionContainerRight}
            id={`scq-indicator-container-${idx}`}>

            <img
              id={`scq-indicator-icon-${idx}`}
              className={`${styles?.answerIndicator} ${
              correctAnswerIndicator || wrongAnswerIndicator ?
              styles?.answerIndicatorActive :
              ""}`
              }
              src={correctAnswerIndicator ? CheckMarkCircle : CloseCircle}
              alt={correctAnswerIndicator ? "Correct" : "Wrong"} />

          </div>
        </div>);

    },
    [
    optionStyles,
    value,
    onAnswerSubmittedRef,
    props,
    handleOnChange,
    openImage,
    presentationMode,
    isReviewScreen,
    isFactual,
    layoutType]

  );

  const renderedOptions = useMemo(() => {
    if (!props?.options?.length) return null;

    if (layoutType === "twoRows") {
      const rows = [];
      for (let i = 0; i < props.options.length; i += 2) {
        rows.push(props.options.slice(i, i + 2));
      }

      return (
        <div id="two-rows-wrapper" className={styles.twoRowsWrapper}>
          {rows.map((row, rowIndex) =>
          <div
            id="two-rows-container"
            key={rowIndex}
            className={styles.rowContainer}>

              {row.map((optionContent, idx) => {
              const actualIndex = rowIndex * 2 + idx;
              return (
                <div key={actualIndex}>
                    {renderSingleOption(optionContent, actualIndex, false)}
                  </div>);

            })}
            </div>
          )}
        </div>);

    }

    return props.options.map((optionContent, idx) =>
    renderSingleOption(optionContent, idx, layoutType === "column")
    );
  }, [props.options, layoutType, renderSingleOption]);

  const wrapperClasses = useMemo(() => {
    const classes = [styles.optionsWrapper];

    if (layoutType === "column") classes.push(styles.columnWrapper);
    if (layoutType === "twoRows") classes.push(styles.twoRowsContainer);

    return classes.join(" ");
  }, [layoutType]);

  // Show loading while hashing is in progress
  if (!isHashingComplete) {
    return <div>Loading options...</div>;
  }

  return (
    <div
      id="scq-wrapper"
      style={props?.isDontAllow ? { pointerEvents: "none" } : {}}>

      <div id="scq-options-container" className={wrapperClasses}>
        {renderedOptions}
      </div>
      {isOpen &&
      <div id="scq-image-modal-wrapper" style={{ background: "blue" }}>
          <ImageModal
          key={Math.random()}
          isOpen={isOpen}
          handleClose={handleClose}
          url={props.option_image_details?.[imageIndex]?.url} />

        </div>
      }
    </div>);

};

export default ScqComponent;